import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import Group from 'components/utils/Group'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { isHotelPackage } from 'lib/offer/offerTypes'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { LABEL_HEIGHT } from 'LuxKit/Label/Label'
import { rem } from 'polished'

const FlexGroup = styled(Group)`
  flex: 1;
`

const WrappableLabel = styled(StructureLabel)`
  white-space: normal;
  height: unset;
  min-height: ${LABEL_HEIGHT};
  padding-block: ${rem(2)};
`

interface RoomPackageDetails {
  size?: string
  capacity?: string
  beds?: string
  wifi?: string
  parking?: string
}

function extractHotelRoomPackageDetailList(roomPackage: App.HotelPackage): RoomPackageDetails {
  const dimension = roomPackage.roomType.sizeSqm
  const beds = (roomPackage.roomType?.bedGroups ?? []).map((b) => b.description).join(' OR ')

  return {
    size: dimension ? `${dimension}m²` : undefined,
    beds: beds || undefined,
  }
}

function extractBedbankRoomPackageMaxCapacities(capacities: Array<App.RoomCapacity>): Omit<App.RoomCapacity, 'infants'> {
  let maxTotal = 0
  let maxAdults = 0
  let maxChildren = 0
  let maxTeenagers = 0

  for (const capacity of capacities) {
    if (capacity.adults > maxAdults) {
      maxAdults = capacity.adults
    }
    if (capacity.teenagers > maxTeenagers) {
      maxTeenagers = capacity.teenagers
    }
    if (capacity.children > maxChildren) {
      maxChildren = capacity.children
    }
    const total = capacity.adults + capacity.teenagers + capacity.children
    if (total > maxTotal) {
      maxTotal = total
    }
  }

  return { total: maxTotal, adults: maxAdults, children: maxChildren, teenagers: maxTeenagers }
}
function extractBedbankRoomPackageDetailList(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))
  const maxCapacities = extractBedbankRoomPackageMaxCapacities(roomPackage.capacities.combinations)

  // TODO: Where does roomPackage.bedGroupsDescription go?

  return {
    beds: features.get('bed'),
    capacity: `Sleeps ${maxCapacities.total}`,
    size: features.get('dimension'),
    wifi: features.get('wifi'),
    parking: features.get('parking'),
  }
}

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
}

function AccommodationRoomDetailsList(props: Props) {
  const { roomPackage } = props
  const detailsList = useMemo(() => {
    if (isHotelPackage(roomPackage)) {
      return extractHotelRoomPackageDetailList(roomPackage)
    } else { // is BedbankPackage
      return extractBedbankRoomPackageDetailList(roomPackage)
    }
  }, [roomPackage])

  return <FlexGroup direction="vertical" horizontalAlign="start" gap={4} noDisplayWhenEmpty>
    <LabelGroup>
      {detailsList.size && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>
        {detailsList.size}
      </StructureLabel>}
      {detailsList.capacity && <StructureLabel variant="light" startIcon={<LineUsersAltIcon />}>
        {detailsList.capacity}
      </StructureLabel>}
      {detailsList.beds && <WrappableLabel variant="light" startIcon={<LineBedIcon />}>
        {detailsList.beds}
      </WrappableLabel>}
    </LabelGroup>
  </FlexGroup>
}

export default AccommodationRoomDetailsList
